import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FastArrowLeft, FastArrowRight, NavArrowLeft, NavArrowRight, Search } from 'iconoir-react';
import { Loader2 } from 'lucide-react';

import { ConversationContextMenu } from 'components/blocks/layout/conversation-context-menu';
import type { SidebarItem } from 'components/blocks/layout/types/sidebar-conversation';
import { useTheme } from 'components/core/theme-provider';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { useConversationContextMenu } from 'features/chat/hooks/use-conversation-context-menu';
import { useConfig } from 'hooks/use-config';
import { cn } from 'lib/utils';
import { useChatStore } from 'state/store/chat/use-chat-store';
import { getConversationDisplayTitle } from 'utils/chat-utilities';
import type { modalOption } from '/models/sidebar';

import { formatChatHistoryDate } from './utils';

const CHAT_HISTORY_PAGE_SIZE = 20;

const chatHistoryPaginationButtonClassName =
  'h-8 w-8 rounded-full border border-grey-100 bg-true-white p-0 shadow-none hover:bg-true-white disabled:border-grey-100 disabled:bg-true-white disabled:text-grey-250 dark:border-border dark:bg-card dark:hover:bg-card dark:disabled:border-border dark:disabled:bg-card';

const chatHistoryRowTextClassName =
  'font-sans text-sm font-normal not-italic leading-body tracking-normal sm:text-base';

const chatHistoryHeaderTextClassName =
  'font-sans text-sm font-bold not-italic leading-heading tracking-normal';

const desktopTableGridClassName =
  'grid-cols-[minmax(0,1fr)_12rem_1.5rem] gap-x-6';

type ChatHistoryRowProps = {
  item: SidebarItem;
  title: string;
  onOpen: () => void;
  menuOptions: modalOption[];
  moreActionsLabel: string;
};

function ChatHistoryRow({
  item,
  title,
  onOpen,
  menuOptions,
  moreActionsLabel,
}: ChatHistoryRowProps) {
  const { theme } = useTheme();
  const formattedDate = formatChatHistoryDate(item.LastUpdatedDate);
  const rowTextClassName = cn(
    chatHistoryRowTextClassName,
    theme === 'dark' ? 'text-foreground' : 'text-grey-700'
  );

  return (
    <>
      <div className="grid w-full min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 border-b border-solid border-grey-50 pb-3 dark:border-border/60 md:hidden">
        <button
          type="button"
          onClick={onOpen}
          className="flex min-h-10 min-w-0 items-center gap-3 text-left"
        >
          <span className={cn('min-w-0 flex-1 truncate', rowTextClassName)}>{title}</span>
          <span className={cn('max-w-[42%] shrink-0 truncate', rowTextClassName)}>
            {formattedDate}
          </span>
        </button>

        <ConversationContextMenu
          variant="page"
          contentSide="bottom"
          contentAlign="end"
          triggerAriaLabel={moreActionsLabel}
          options={menuOptions}
        />
      </div>

      <div
        className={`hidden w-full min-w-0 items-center border-b border-solid border-grey-50 pb-3 dark:border-border/60 md:grid ${desktopTableGridClassName}`}
      >
        <button type="button" onClick={onOpen} className="flex min-w-0 items-center text-left">
          <span className={cn('min-w-0 truncate', rowTextClassName)}>{title}</span>
        </button>

        <p className={cn('min-w-0 truncate', rowTextClassName)}>{formattedDate}</p>

        <div className="flex justify-end">
          <ConversationContextMenu
            variant="page"
            contentSide="bottom"
            contentAlign="end"
            triggerAriaLabel={moreActionsLabel}
            options={menuOptions}
          />
        </div>
      </div>
    </>
  );
}

type ChatHistoryPageProps = {
  /** Lets the search dialog close itself once a conversation is opened. */
  onConversationOpen?: () => void;
};

export default function ChatHistoryPage({ onConversationOpen }: ChatHistoryPageProps = {}) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { configurationSettings } = useConfig();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const isDark = theme === 'dark';
  const headerTextClassName = cn(
    chatHistoryHeaderTextClassName,
    isDark ? 'text-muted-foreground' : 'text-grey-700'
  );
  const titleTextClassName = isDark ? 'text-true-white' : 'text-grey-900';
  const searchTextClassName = isDark ? 'text-foreground' : 'text-grey-900';
  const emptyTextClassName = isDark ? 'text-muted-foreground' : 'text-grey-400';
  const loaderClassName = isDark ? 'text-true-white' : 'text-primary';
  const paginationTextClassName = isDark ? 'text-foreground' : 'text-grey-900';
  const paginationButtonClassName = cn(
    chatHistoryPaginationButtonClassName,
    isDark
      ? 'text-foreground hover:text-foreground disabled:text-muted-foreground'
      : 'text-grey-900 hover:text-grey-900'
  );

  const fetchSearchResults = useChatStore((state) => state.fetchSearchResults);
  const conversations = useChatStore((state) => state.searchConversationsList);
  const totalCount = useChatStore((state) => state.searchConversationsTotalCount);
  const { getMenuOptions, conversationMenuDialogs } = useConversationContextMenu();

  const pageCount = Math.max(1, Math.ceil(totalCount / CHAT_HISTORY_PAGE_SIZE));

  useEffect(() => {
    if (!isLoading && totalCount > 0 && page > pageCount) {
      setPage(pageCount);
    }
  }, [page, pageCount, isLoading, totalCount]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      await fetchSearchResults(search, configurationSettings, {
        page,
        pageSize: CHAT_HISTORY_PAGE_SIZE,
      });
      if (!cancelled) {
        setIsLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [search, page, fetchSearchResults, configurationSettings]);

  const filteredConversations = useMemo(
    () => conversations.filter((item) => !item.AgentId),
    [conversations]
  );

  const handleOpenConversation = (item: SidebarItem) => {
    if (item.ProjectId) {
      navigate(`/chat/${item.ConversationId}/${item.ProjectId}`);
    } else {
      navigate(`/chat/${item.ConversationId}`);
    }
    onConversationOpen?.();
  };

  return (
    <div data-testid="chat-history" className="flex min-h-0 flex-1 flex-col overflow-hidden py-1">
      <div className="mx-auto flex min-h-0 w-full min-w-0 max-w-chat flex-1 flex-col gap-6 md:gap-10 lg:gap-15">
        <div className="flex w-full shrink-0 flex-col gap-6 md:gap-8">
          <div className="flex w-full items-center justify-between">
            <h1
              className={cn(
                'shrink-0 font-sans text-xl font-medium not-italic leading-heading tracking-normal sm:text-2xl',
                titleTextClassName
              )}
            >
              {t('CHAT_HISTORY', 'Chat History')}
            </h1>
          </div>

          <div className="flex h-12 w-full min-w-0 items-center gap-3 rounded-lg border border-grey-100 bg-white p-3 dark:border-border dark:bg-card">
            <div className="flex h-6 w-6 shrink-0 items-center justify-center">
              <Search width={15} height={15} strokeWidth={1.75} className="text-grey-400" />
            </div>
            <Input
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              placeholder={t('SEARCH_CHATS', 'Search chats...')}
              className={cn(
                'h-full min-h-0 flex-1 border-0 bg-transparent p-0 font-sans text-base font-normal leading-heading shadow-none placeholder:text-grey-400 focus-visible:ring-0 md:text-sm dark:bg-transparent',
                searchTextClassName
              )}
            />
          </div>
        </div>

        <div className="flex min-h-0 w-full flex-1 flex-col gap-4 md:gap-6">
          <div className={`shrink-0 md:hidden ${headerTextClassName}`}>{t('CHAT', 'Chat')}</div>

          <div
            className={`hidden w-full min-w-0 shrink-0 items-center overflow-y-hidden [scrollbar-gutter:stable] md:grid md:pr-6 ${desktopTableGridClassName}`}
          >
            <p className={headerTextClassName}>{t('CHAT', 'Chat')}</p>
            <p className={headerTextClassName}>{t('DATE', 'Date')}</p>
            <span className="w-6" aria-hidden />
          </div>

          <div
            data-testid="chat-history-rows"
            className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overflow-x-hidden pb-6 pr-4 [scrollbar-gutter:stable] md:gap-5 md:pr-6"
          >
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className={cn('h-8 w-8 animate-spin', loaderClassName)} />
              </div>
            ) : filteredConversations.length === 0 ? (
              <p
                className={cn(
                  'py-8 font-sans text-sm font-normal leading-body sm:text-base',
                  emptyTextClassName
                )}
              >
                {t('NO_RESULTS_FOUND', 'No results found')}
              </p>
            ) : (
              <>
                {filteredConversations.map((item) => {
                  const title = getConversationDisplayTitle(
                    item.ConversationTitle,
                    t('NEW_CHAT', 'New chat')
                  );

                  return (
                    <ChatHistoryRow
                      key={item.ConversationId}
                      item={item}
                      title={title}
                      onOpen={() => handleOpenConversation(item)}
                      menuOptions={getMenuOptions(item.ConversationId, title, item.IsGroup)}
                      moreActionsLabel={t('MORE_ACTIONS', 'More actions')}
                    />
                  );
                })}

                {totalCount > 0 ? (
                  <div className="mt-2 flex w-full items-center justify-end gap-4 md:mt-6">
                    <p
                      className={cn('text-sm font-normal leading-heading', paginationTextClassName)}
                    >
                      {t('PAGE_OF', {
                        page,
                        total: pageCount,
                        defaultValue: `Page ${page} of ${pageCount}`,
                      })}
                    </p>

                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className={paginationButtonClassName}
                        onClick={() => setPage(1)}
                        disabled={page <= 1}
                        aria-label={t('GO_TO_FIRST_PAGE', 'Go to first page')}
                      >
                        <FastArrowLeft width={16} height={16} strokeWidth={1.75} />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className={paginationButtonClassName}
                        onClick={() => setPage((current) => Math.max(1, current - 1))}
                        disabled={page <= 1}
                        aria-label={t('GO_TO_PREVIOUS_PAGE', 'Go to previous page')}
                      >
                        <NavArrowLeft width={16} height={16} strokeWidth={1.75} />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className={paginationButtonClassName}
                        onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
                        disabled={page >= pageCount}
                        aria-label={t('GO_TO_NEXT_PAGE', 'Go to next page')}
                      >
                        <NavArrowRight width={16} height={16} strokeWidth={1.75} />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className={paginationButtonClassName}
                        onClick={() => setPage(pageCount)}
                        disabled={page >= pageCount}
                        aria-label={t('GO_TO_LAST_PAGE', 'Go to last page')}
                      >
                        <FastArrowRight width={16} height={16} strokeWidth={1.75} />
                      </Button>
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>

        {conversationMenuDialogs}
      </div>
    </div>
  );
}
